import map from 'lodash/map';
import merge from 'lodash/merge';
import { COUNTRY_CODES } from '@app/admin/fleet-card/shared/currency-utils';

export interface IState {
  name: string;
  value: string;
}

export const STATES = {
  'United States': {
    AL: $localize`Alabama`,
    AK: $localize`Alaska`,
    AS: $localize`American Samoa`,
    AZ: $localize`Arizona`,
    AR: $localize`Arkansas`,
    CA: $localize`California`,
    CO: $localize`Colorado`,
    CT: $localize`Connecticut`,
    DE: $localize`Delaware`,
    DC: $localize`District of Columbia`,
    FL: $localize`Florida`,
    GA: $localize`Georgia`,
    GU: $localize`Guam`,
    HI: $localize`Hawaii`,
    ID: $localize`Idaho`,
    IL: $localize`Illinois`,
    IN: $localize`Indiana`,
    IA: $localize`Iowa`,
    KS: $localize`Kansas`,
    KY: $localize`Kentucky`,
    LA: $localize`Louisiana`,
    ME: $localize`Maine`,
    MD: $localize`Maryland`,
    MA: $localize`Massachusetts`,
    MI: $localize`Michigan`,
    MN: $localize`Minnesota`,
    MS: $localize`Mississippi`,
    MO: $localize`Missouri`,
    MT: $localize`Montana`,
    NE: $localize`Nebraska`,
    NV: $localize`Nevada`,
    NH: $localize`New Hampshire`,
    NJ: $localize`New Jersey`,
    NM: $localize`New Mexico`,
    NY: $localize`New York`,
    NC: $localize`North Carolina`,
    ND: $localize`North Dakota`,
    OH: $localize`Ohio`,
    OK: $localize`Oklahoma`,
    OR: $localize`Oregon`,
    PA: $localize`Pennsylvania`,
    PR: $localize`Puerto Rico`,
    RI: $localize`Rhode Island`,
    SC: $localize`South Carolina`,
    SD: $localize`South Dakota`,
    TN: $localize`Tennessee`,
    TX: $localize`Texas`,
    UT: $localize`Utah`,
    VT: $localize`Vermont`,
    VA: $localize`Virginia`,
    VI: $localize`Virgin Islands`,
    WA: $localize`Washington`,
    WV: $localize`West Virginia`,
    WI: $localize`Wisconsin`,
    WY: $localize`Wyoming`,
  },
  'Canada': {
    AB: $localize`Alberta`,
    BC: $localize`British Columbia`,
    MB: $localize`Manitoba`,
    NB: $localize`New Brunswick`,
    NL: $localize`Newfoundland and Labrador`,
    NT: $localize`Northwest Territories`,
    NS: $localize`Nova Scotia`,
    NU: $localize`Nunavut`,
    ON: $localize`Ontario`,
    PE: $localize`Prince Edward Island`,
    QC: $localize`Quebec`,
    SK: $localize`Saskatchewan`,
    YT: $localize`Yukon`,
  },
  'Mexico': {
    AG: $localize`Aguascalientes`,
    BN: $localize`Baja California Norte`,
    BS: $localize`Baja California Sur`,
    CS: $localize`Chiapas`,
    CP: $localize`Campeche`,
    CH: $localize`Coahuila`,
    CI: $localize`Chihuahua`,
    CL: $localize`Colima`,
    DF: $localize`Ciudad de México`,
    DG: $localize`Durango`,
    GE: $localize`Guerrero`,
    GJ: $localize`Guanajuato`,
    HD: $localize`Hidalgo`,
    JA: $localize`Jalisco`,
    MX: $localize`Estado de México`,
    MC: $localize`Michoacan`,
    MR: $localize`Morelos`,
    NA: $localize`Nayarit`,
    NLE: $localize`Nuevo Leon`,
    OA: $localize`Oaxaca`,
    PU: $localize`Puebla`,
    QI: $localize`Quintana Roo`,
    QE: $localize`Queretaro`,
    SL: $localize`San Luis Potosi`,
    SI: $localize`Sinaloa`,
    SO: $localize`Sonora`,
    TB: $localize`Tabasco`,
    TA: $localize`Tamaulipas`,
    TL: $localize`Tlaxcala`,
    VC: $localize`Veracruz`,
    YU: $localize`Yucatan`,
    ZA: $localize`Zacatecas`,
  },
};

export const OTHER_STATE = {
  OT: $localize`Other`,
};

export const OTHER_STATE_ALLOWED_COUNTRIES = {
  us: true,
  canada: true,
  all: true,
  mexico: false,
}

export function getStates(country: 'us' | 'canada' | 'mexico' | 'all', addOtherOption = false): IState[] {
  const statesMapping = {
    us: STATES['United States'],
    canada: STATES['Canada'],
    mexico: STATES['Mexico'],
    all: merge(
      {},
      STATES['United States'],
      STATES['Canada']
    ),
  };

  return transform(merge({}, statesMapping[country], addOtherOption && OTHER_STATE_ALLOWED_COUNTRIES[country] ? OTHER_STATE : {}));
}

export function getStatesByCountryCode(countryCode: COUNTRY_CODES | string): IState[] {
  switch (countryCode) {
    case COUNTRY_CODES.US:
      return getStates('us');
    case COUNTRY_CODES.CA:
      return getStates('canada');
    default:
      return getStates('all');
  }
}

export function getState(country: string, state: string): string {
  if (state === 'OT') return OTHER_STATE['OT'];

  return (STATES as Record<string, Record<string, string>>)[country]?.[state];
}

type StatesCountryKey = keyof typeof STATES;

function getStateNameByCode(countryKey: StatesCountryKey, stateCode: string): string | undefined {
  return (STATES[countryKey] as Record<string, string>)[stateCode];
}

function getStatesCountryKey(countryCode?: string): StatesCountryKey | null {
  if (!countryCode) {
    return null;
  }

  switch (countryCode.trim().toUpperCase()) {
    case COUNTRY_CODES.US:
      return 'United States';
    case COUNTRY_CODES.CA:
      return 'Canada';
    case 'MX':
      return 'Mexico';
    default:
      return null;
  }
}

/**
 * Returns the localized full state/province name for a code (e.g. "IL" → "Illinois").
 * When `countryCode` is provided, lookup is scoped to that country first.
 * Unknown values (or already-full names) are returned trimmed as-is.
 */
export function getStateDisplayName(stateCodeOrName: string, countryCode?: string): string {
  if (!stateCodeOrName) {
    return '';
  }

  const trimmed: string = stateCodeOrName.trim();
  if (!trimmed) {
    return '';
  }

  const normalizedCode: string = trimmed.toUpperCase();
  if (normalizedCode === 'OT') {
    return OTHER_STATE['OT'];
  }

  const countryKey: StatesCountryKey | null = getStatesCountryKey(countryCode);
  if (countryKey) {
    const scopedName: string | undefined = getStateNameByCode(countryKey, normalizedCode);
    if (scopedName) {
      return scopedName;
    }
  }

  const countryKeys: StatesCountryKey[] = Object.keys(STATES) as StatesCountryKey[];
  for (const country of countryKeys) {
    const name: string | undefined = getStateNameByCode(country, normalizedCode);
    if (name) {
      return name;
    }
  }

  return trimmed;
}

export function getCountryFromState(state: string): string {
  const countryKeys: StatesCountryKey[] = Object.keys(STATES) as StatesCountryKey[];
  for (const country of countryKeys) {
    if (Object.prototype.hasOwnProperty.call(STATES[country], state)) {
      return country;
    }
  }
  return null; // Return null if stateKey is not found
}

export function getCountryCodeFromState(state: string): string {
  const countryName = getCountryFromState(state);
  const countryMap: Record<StatesCountryKey, string> = {
    'United States': 'US',
    'Canada': 'CA',
    'Mexico': 'MX',
  };

  return (countryName && countryMap[countryName as StatesCountryKey]) || '';
}

export function getStateCodeFromName(stateName: string): string {
  if (stateName.toLowerCase() === OTHER_STATE['OT'].toLowerCase()) return 'OT';

  const countryKeys: StatesCountryKey[] = Object.keys(STATES) as StatesCountryKey[];
  for (const country of countryKeys) {
    const statesInCountry: Record<string, string> = STATES[country] as Record<string, string>;
    for (const stateCode of Object.keys(statesInCountry)) {
      if (statesInCountry[stateCode].toLowerCase() === stateName.toLowerCase()) {
        return stateCode;
      }
    }
  }
  return '';
}

function transform(items: Record<string, string>): IState[] {
  return map(items, (name: string, value: string) => {
    return { name, value };
  });
}
